<script lang="ts">
	import Layout from "@components/layout/Layout.svelte";
	import { tick } from "svelte";
	import { getCatalogueCategoryPath } from "@core/schema/paths/getCatalogueCategoryPath.js";
	import { getCataloguePath } from "@core/schema/paths/getCataloguePath.js";
	import { getProductPath } from "@core/schema/paths/getProductPath.js";
	import { getUniqueProductCategories } from "@core/utils/getUniqueProductCategories.js";
	import heroPhotoMobile from "../assets/final_photos/DSC_0256.jpg?webp&h=540&imagetools";
	import heroPhoto from "../assets/final_photos/DSC_0256.jpg?webp&h=1080&imagetools";
	import ContactForm from "../components/ContactForm.svelte";
	import PageHero from "../components/PageHero.svelte";
	import Formatted from "../components/common/Formatted.svelte";
	import Link from "../components/common/Link.svelte";
	import Media from "../components/common/Media.svelte";
	import StickySection from "../components/common/StickySection.svelte";
	import TextWithIcon from "../components/common/TextWithIcon.svelte";
	import { ButtonVariant } from "../components/common/getButtonClass.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import type { CatalogueProps } from "./CatalogueProps.js";
	import translations from "./catalogue.translations.json" with { type: "json" };

	const { tenant } = getGlobalTemplateProps();
	const { products, activeCategory = null }: CatalogueProps = $props();

	const translate = getTranslate(translations);

	const allCategories = getUniqueProductCategories(products);

	const categoriesWithCounts = allCategories.map((category) => ({
		name: category,
		count: products.filter((p) => p.parentProductCategory === category).length,
	}));

	const mainCategories = categoriesWithCounts.filter((cat) => cat.count > 3);
	const otherCategories = categoriesWithCounts.filter((cat) => cat.count <= 3);

	// Initialize selectedCategory from URL path or activeCategory prop
	const getInitialCategory = (): string | null => {
		if (typeof window !== "undefined") {
			const currentPath = window.location.pathname;
			const baseCataloguePath = getCataloguePath(tenant.locale);

			if (currentPath.startsWith(`${baseCataloguePath}/`)) {
				const foundCategory = allCategories.find((cat) => {
					const expectedPath = getCatalogueCategoryPath(tenant.locale, cat);
					return expectedPath === currentPath;
				});
				if (foundCategory) {
					return foundCategory;
				}
			}
		}
		return activeCategory;
	};

	let selectedCategory = $state<string | null>(getInitialCategory());
	let showOtherCategories = $state<boolean>(false);
	let mobileFilterOpen = $state<boolean>(false);

	$effect(() => {
		showOtherCategories = selectedCategory ? otherCategories.some((cat) => cat.name === selectedCategory) : false;
	});

	const filteredProducts = $derived(() => {
		if (!selectedCategory) {
			return products;
		}
		return products.filter((product) => product.parentProductCategory === selectedCategory);
	});

	async function scrollToProducts(): Promise<void> {
		await tick();
		const productsSection = document.getElementById("products-section");
		if (productsSection) {
			productsSection.scrollIntoView({ behavior: "smooth" });
		}
	}

	function selectAllProducts(): void {
		selectedCategory = null;
		mobileFilterOpen = false;
		const newPath = getCataloguePath(tenant.locale);
		window.history.pushState({}, "", newPath);
		void scrollToProducts();
	}

	function selectCategory(name: string): void {
		selectedCategory = name;
		mobileFilterOpen = false;
		const newPath = getCatalogueCategoryPath(tenant.locale, name);
		window.history.pushState({}, "", newPath);
		void scrollToProducts();
	}
</script>

<Layout>
	<PageHero
		image={heroPhoto}
		mobileImage={heroPhotoMobile}
		alt={translate("heroAlt")}
		heading={activeCategory ?? translate("heroHeading")}
		text={translate("heroText")}
		text2={translate("heroText2")}
		text3={translate("heroText3")}
		brightness={120}
		objectPosition="center 60%"
	/>
	<section class="full-width grid-container bg-zms-grey">
		<div class="flex flex-col lg:flex-row gap-6">
			<StickySection>
				<!-- Mobile: collapsible filter panel -->
				<div class="lg:hidden">
					<button
						class="w-full flex items-center justify-between p-3 bg-white border border-zms-blue/30 rounded-lg text-sm font-semibold text-zms-optical-black"
						onclick={(): void => {
							mobileFilterOpen = !mobileFilterOpen;
						}}
					>
						<span>{selectedCategory ?? translate("allProducts")} ({filteredProducts().length})</span>
						<span
							class="text-xs text-zms-blue transform transition-transform duration-200"
							class:rotate-180={mobileFilterOpen}>▼</span
						>
					</button>
					{#if mobileFilterOpen}
						<div
							class="mt-1 bg-white border border-zms-blue/30 rounded-lg overflow-hidden max-h-[50dvh] overflow-y-auto"
						>
							<button
								class={[
									"w-full flex justify-between items-center gap-2 px-3 py-3 text-sm font-semibold text-start border-b border-zms-blue/10 transition-colors duration-200 min-h-[48px]",
									!selectedCategory ? "bg-[#CCCCFF]" : "hover:bg-[#CCCCFF]/50",
								]}
								onclick={selectAllProducts}
							>
								<span>{translate("allProducts")}</span>
								<span
									class="text-[.625rem]/[1] py-0.5 px-2 border-zms-blue border rounded-full font-semibold text-zms-blue"
									>{products.length}</span
								>
							</button>
							{#each [...mainCategories, ...otherCategories] as category (category.name)}
								<button
									class={[
										"w-full flex justify-between items-center gap-2 px-3 py-3 text-sm font-semibold text-start border-b border-zms-blue/10 transition-colors duration-200 min-h-[48px]",
										selectedCategory === category.name ? "bg-[#CCCCFF]" : "hover:bg-[#CCCCFF]/50",
									]}
									onclick={(): void => {
										selectCategory(category.name);
									}}
								>
									<span>{category.name}</span>
									<span
										class="text-[.625rem]/[1] py-0.5 px-2 border-zms-blue border rounded-full font-semibold text-zms-blue"
										>{category.count}</span
									>
								</button>
							{/each}
						</div>
					{/if}
				</div>
				<!-- Desktop: vertical list with scroll -->
				<div class="hidden lg:block w-full flex-shrink-0 max-h-[calc(100dvh-88px)] overflow-y-auto">
					<button
						class={[
							"w-full flex justify-between p-2 border-t-zms-blue border-t duration-300 transition-colors items-center gap-2 lg:max-w-[11.625rem] text-start",
							[!selectedCategory ? "bg-[#CCCCFF] hover:bg-[#AAAAFF]" : "hover:bg-[#CCCCFF]"],
						]}
						onclick={selectAllProducts}
					>
						<span class="text-sm/[1.5] font-semibold text-zms-optical-black">{translate("allProducts")}</span>
						<span
							class="text-[.625rem]/[1] py-[.375rem] px-[.75rem] border-zms-blue border rounded-full font-semibold text-zms-blue"
							>{products.length}</span
						>
					</button>
					{#each mainCategories as category (category.name)}
						<button
							class={[
								"w-full flex justify-between p-2 border-t-zms-blue border-t duration-300 transition-colors items-center gap-2 lg:max-w-[11.625rem] text-start",
								[selectedCategory === category.name ? "bg-[#CCCCFF] hover:bg-[#AAAAFF]" : "hover:bg-[#CCCCFF]"],
							]}
							onclick={(): void => {
								selectCategory(category.name);
							}}
						>
							<span class="text-sm/[1.5] font-semibold text-zms-optical-black">{category.name}</span>
							<span
								class="text-[.625rem]/[1] py-[.375rem] px-[.75rem] border-zms-blue border rounded-full font-semibold text-zms-blue"
								>{category.count}</span
							>
						</button>
					{/each}

					{#if otherCategories.length > 0}
						<div>
							<button
								class={[
									"w-full flex justify-between p-2 border-t-zms-blue border-t duration-300 transition-colors items-center gap-2 lg:max-w-[11.625rem] text-start",
									[showOtherCategories ? "bg-[#CCCCFF] hover:bg-[#AAAAFF]" : "hover:bg-[#CCCCFF]"],
								]}
								onclick={(): void => {
									showOtherCategories = !showOtherCategories;
								}}
							>
								<span class="text-sm/[1.5] font-semibold text-zms-optical-black">{translate("otherCategories")}</span>
								<div class="flex items-center gap-2">
									<span
										class="text-[.625rem]/[1] py-[.375rem] px-[.75rem] border-zms-blue border rounded-full font-semibold text-zms-blue"
										>{otherCategories.reduce((sum, cat) => sum + cat.count, 0)}</span
									>
									<span
										class="text-[.625rem]/[1] text-zms-blue transform transition-transform"
										class:rotate-180={showOtherCategories}>▼</span
									>
								</div>
							</button>

							{#if showOtherCategories}
								{#each otherCategories as category (category.name)}
									<button
										class={[
											"w-full flex justify-between p-2 border-t-zms-blue border-t duration-300 transition-colors items-center gap-2 lg:max-w-[11.625rem] text-start",
											[selectedCategory === category.name ? "bg-[#CCCCFF] hover:bg-[#AAAAFF]" : "hover:bg-[#CCCCFF]"],
										]}
										onclick={(): void => {
											selectCategory(category.name);
										}}
									>
										<span class="text-sm/[1.5] font-semibold text-zms-optical-black">{category.name}</span>
										<span
											class="text-[.625rem]/[1] py-[.375rem] px-[.75rem] border-zms-blue border rounded-full font-semibold text-zms-blue"
											>{category.count}</span
										>
									</button>
								{/each}
							{/if}
						</div>
					{/if}
				</div>
			</StickySection>
			<div class="flex-1" id="products-section">
				<div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
					{#each filteredProducts() as product (product.id)}
						<div
							class="bg-white rounded-2xl lg:h-[29.375rem] h-full flex flex-col p-4 md:p-6 overflow-hidden shadow-sm hover:shadow-md transition-shadow border"
						>
							{#if product.gallery.length > 0 && product.gallery[0]}
								<div class="aspect-square rounded-[.25rem] w-1/2 overflow-hidden bg-gray-50 flex-shrink-0">
									<Link href={getProductPath(tenant.locale, product.urlSlug)} class="hover:opacity-100">
										<Media
											wrapperClass="w-full h-full"
											class="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
											height={300}
											width={300}
											alt={product.name}
											source={product.gallery[0]}
										/>
									</Link>
								</div>
							{:else}
								<div class="aspect-square rounded-[.25rem] w-1/2 overflow-hidden bg-gray-50 flex-shrink-0">
									<div class="w-full h-full bg-grey flex items-center justify-center"></div>
								</div>
							{/if}
							<div class="flex flex-col justify-between gap-4 mt-8 flex-1 min-h-0">
								<div class="flex flex-col gap-5 flex-1 min-h-0">
									<h2 class="font-medium line-clamp-2 text-lg/[1.2] text-zms-blue-dark">
										<Link
											href={getProductPath(tenant.locale, product.urlSlug)}
											variant={ButtonVariant.Transparent}
											class="hover:opacity-100"
										>
											<Formatted text={product.name} />
										</Link>
									</h2>
									{#if product.perex}
										<p class="text-sm text-gray-600 line-clamp-5 overflow-hidden">
											<Formatted text={product.perex} />
										</p>
									{/if}
								</div>
								<div class="flex-shrink-0 mt-auto">
									<Link
										href={getProductPath(tenant.locale, product.urlSlug)}
										variant={ButtonVariant.TransparentWithIcon}
									>
										<TextWithIcon>
											{translate("productDetail")}
										</TextWithIcon>
									</Link>
								</div>
							</div>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</section>
	<ContactForm heading="default" />
</Layout>
